import React, { useEffect, useRef, useState, memo } from "react";
import { Scene, ImageContainer, Image, AppWrapper } from "../styles/3D/styles";
import THREEJS from "./three";
import { cubeData } from "./cubes/data";
import { cubeAnimations } from "./cubes/animations";
import { rippleShader } from "./shader/ripple";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import { COLOURS } from "../styles/variables/colours";

const ThreeJsComponent = ({ disableLoader }) => {
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(true);

  const mountRef = useRef(null);
  const cameraRef = useRef(null);
  const secondaryCameraRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const secondarySceneRef = useRef(null);
  const controlsRef = useRef(null);
  const statsRef = useRef(null);
  const cubeRef = useRef(null);
  const composerRef = useRef(null);

  let enableStats = false;
  let enableGui = false;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isMobile = window.innerWidth < BREAKPOINTS.sm;

    if (!THREEJS.WebGLRenderer) {
      console.error("WebGL is not supported in this browser");
      setIsWebGLAvailable(false);
      return;
    }

    const currentMount = mountRef.current;

    const loadingManager = new THREEJS.LoadingManager();
    loadingManager.onLoad = () => {
      disableLoader();
    };
    loadingManager.onError = url => {
      console.error(`There was an error loading: ${url}`);
    };

    sceneRef.current = new THREEJS.Scene();

    secondarySceneRef.current = new THREEJS.Scene();

    secondarySceneRef.current.background = new THREEJS.Color(
      isMobile ? COLOURS.primary.white : COLOURS.primary.blue
    );

    let mouse = new THREEJS.Vector2(0, 0);
    let prevMouse = new THREEJS.Vector2(0, 0);
    let currentWave = 0;

    let max = 50;

    const width = window.innerWidth;
    const height = isMobile
      ? window.screen?.height || window.innerHeight
      : window.innerHeight;

    cameraRef.current = new THREEJS.PerspectiveCamera(
      75,
      width / height,
      0.1,
      1000
    );

    let frustumSize = height;
    let aspect = width / height;
    secondaryCameraRef.current = new THREEJS.OrthographicCamera(
      (frustumSize * aspect) / -2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      frustumSize / -2,
      -1000,
      1000
    );

    rendererRef.current = new THREEJS.WebGLRenderer();
    rendererRef.current.setSize(
      window.innerWidth,
      isMobile
        ? window.screen?.height || window.innerHeight
        : window.innerHeight
    );
    currentMount.appendChild(rendererRef.current.domElement);

    let cubeRenderTarget = new THREEJS.WebGLRenderTarget(
      window.innerWidth,
      isMobile
        ? window.screen?.height || window.innerHeight
        : window.innerHeight,
      {
        minFilter: THREEJS.LinearFilter,
        magFilter: THREEJS.NearestFilter,
        format: THREEJS.RGBAFormat,
      }
    );

    let brushRenderTarget = new THREEJS.WebGLRenderTarget(
      window.innerWidth,
      isMobile
        ? window.screen?.height || window.innerHeight
        : window.innerHeight,
      {
        minFilter: THREEJS.LinearFilter,
        magFilter: THREEJS.NearestFilter,
        format: THREEJS.RGBAFormat,
      }
    );

    let geometry;

    if (width < BREAKPOINTS.sm) {
      geometry = new THREEJS.BoxGeometry(1, 2, 1);
    } else if (width <= BREAKPOINTS.md) {
      geometry =
        width <= BREAKPOINTS.sm
          ? new THREEJS.BoxGeometry(1, 2, 1)
          : new THREEJS.BoxGeometry(1.5, 1.5, 1);
    } else if (width <= BREAKPOINTS.lg) {
      geometry = new THREEJS.BoxGeometry(2, 1, 1);
    } else {
      geometry = new THREEJS.BoxGeometry(2, 1, 1);
    }

    const loader = new THREEJS.TextureLoader(loadingManager);

    const createVideoMaterial = videoURL => {
      const video = document.createElement("video");
      video.src = videoURL;
      video.crossOrigin = "anonymous";
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;

      const videoTexture = new THREEJS.VideoTexture(video);
      videoTexture.minFilter = THREEJS.LinearMipMapLinearFilter;
      videoTexture.magFilter = THREEJS.LinearFilter;
      videoTexture.format = THREEJS.RGBAFormat;
      videoTexture.anisotropy = rendererRef.current.capabilities.getMaxAnisotropy();
      videoTexture.wrapS = THREEJS.ClampToEdgeWrapping;
      videoTexture.wrapT = THREEJS.ClampToEdgeWrapping;

      videoTexture.generateMipmaps = true;

      video
        .play()
        .catch(err => console.error(`Failed to play video: ${videoURL}`, err));
      video.onerror = () => console.error(`Failed to load video: ${videoURL}`);

      return new THREEJS.MeshBasicMaterial({ map: videoTexture });
    };

    const createTextureMaterial = textureURL => {
      const texture = loader.load(
        textureURL,
        loadedTexture => {
          const imageWidth = loadedTexture.image.width;
          const imageHeight = loadedTexture.image.height;

          const aspect =
            window.innerWidth /
            (isMobile
              ? window.screen?.height || window.innerHeight
              : window.innerHeight);
          const imageAspect = imageWidth / imageHeight;

          if (aspect < imageAspect) {
            loadedTexture.matrixAutoUpdate = false;
            loadedTexture.matrix.setUvTransform(
              0,
              0,
              aspect / imageAspect,
              1,
              0,
              0.5,
              0.5
            );
          } else {
            loadedTexture.matrixAutoUpdate = false;
            loadedTexture.matrix.setUvTransform(
              0,
              0,
              1,
              imageAspect / aspect,
              0,
              0.5,
              0.5
            );
          }
        },
        undefined,
        error => console.error(`Error loading texture: ${textureURL}`, error)
      );

      texture.minFilter = THREEJS.LinearMipMapLinearFilter;
      texture.magFilter = THREEJS.LinearFilter;
      texture.anisotropy = rendererRef.current.capabilities.getMaxAnisotropy();

      return new THREEJS.MeshBasicMaterial({ map: texture });
    };

    const materials = Object.values(cubeData.sides).map(side => {
      const videoURL = isMobile ? side.video?.mobile : side.video?.desktop;
      const textureURL = isMobile
        ? side.textures?.mobile
        : side.textures?.desktop;

      if (videoURL) {
        return createVideoMaterial(videoURL);
      } else if (textureURL) {
        return createTextureMaterial(textureURL);
      } else {
        return new THREEJS.MeshBasicMaterial({ color: 0x0004ff });
      }
    });

    cubeRef.current = new THREEJS.Mesh(geometry, materials);
    secondarySceneRef.current.add(cubeRef.current);

    const updateMousePosition = (x, y) => {
      mouse.x = x - width / 2;
      mouse.y = height / 2 - y;
    };

    const handleMouseMove = e => {
      updateMousePosition(e.clientX, e.clientY);
    };

    const handleTouchMove = e => {
      if (e.touches && e.touches[0]) {
        updateMousePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const secondaryGeometry = new THREEJS.PlaneGeometry(64, 64, 1, 1);
    let meshes = [];

    for (let i = 0; i < max; i++) {
      let m = new THREEJS.MeshBasicMaterial({
        map: new THREEJS.TextureLoader().load(
          "https://www.datocms-assets.com/40484/1732910670-brush.png"
        ),
        transparent: true,
        opacity: 1.0,
        blending: THREEJS.AdditiveBlending,
        depthTest: false,
        depthWrite: false,
      });
      let mesh = new THREEJS.Mesh(secondaryGeometry, m);
      mesh.visible = false;
      mesh.rotation.z = 2 * Math.random() * Math.PI;
      sceneRef.current.add(mesh);
      meshes.push(mesh);
    }

    cameraRef.current.position.set(0, 0, 6.5);
    cameraRef.current.lookAt(0, 0, 0);

    controlsRef.current = new THREEJS.OrbitControls(
      cameraRef.current,
      rendererRef.current.domElement
    );

    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.enableZoom = false;
    controlsRef.current.enablePan = false;
    controlsRef.current.enableRotate = false;

    const setNewWave = (x, y, index) => {
      let m = meshes[index];
      m.visible = true;
      m.position.x = x;
      m.position.y = y;
      m.scale.x = m.scale.y = 0.2;
      m.material.opacity = 0.5;
    };

    const trackMousePosition = () => {
      if (mouse.x === prevMouse.x && mouse.y === prevMouse.y) {
        return;
      }
      setNewWave(mouse.x, mouse.y, currentWave);
      currentWave = (currentWave + 1) % max;

      prevMouse.x = mouse.x;
      prevMouse.y = mouse.y;
    };

    composerRef.current = new THREEJS.EffectComposer(rendererRef.current);

    const boxPass = new THREEJS.RenderPass(
      secondarySceneRef.current,
      cameraRef.current
    );
    boxPass.clear = true;
    composerRef.current.addPass(boxPass);

    const brushStrokesPass = new THREEJS.RenderPass(
      sceneRef.current,
      secondaryCameraRef.current
    );
    brushStrokesPass.clear = false;
    composerRef.current.addPass(brushStrokesPass);

    const ripplePass = new THREEJS.ShaderPass(rippleShader);
    composerRef.current.addPass(ripplePass);

    const outputPass = new THREEJS.OutputPass();
    composerRef.current.addPass(outputPass);

    if (enableStats) {
      statsRef.current = new THREEJS.Stats();
      currentMount.appendChild(statsRef.current.dom);
    }

    cubeAnimations({
      cube: cubeRef.current,
      scene: secondarySceneRef.current,
    });

    const render = () => {
      trackMousePosition();

      rendererRef.current.setRenderTarget(cubeRenderTarget);
      rendererRef.current.clear();
      rendererRef.current.render(secondarySceneRef.current, cameraRef.current);

      rendererRef.current.setRenderTarget(brushRenderTarget);
      rendererRef.current.clear();
      rendererRef.current.render(sceneRef.current, secondaryCameraRef.current);

      rendererRef.current.setRenderTarget(null);

      composerRef.current.passes.forEach(pass => {
        if (pass.material && pass.material.uniforms) {
          if (pass.material.uniforms.uTexture) {
            pass.material.uniforms.uTexture.value = cubeRenderTarget.texture;
          }
          if (pass.material.uniforms.uDisplacement) {
            pass.material.uniforms.uDisplacement.value =
              brushRenderTarget.texture;
          }
        }
      });

      composerRef.current.render();

      meshes.forEach((mesh, i) => {
        if (!mesh.visible) return;
        mesh.rotation.z += 0.02;
        mesh.material.opacity *= 0.96;
        mesh.scale.x = 0.982 * mesh.scale.x + 0.108;
        mesh.scale.y = mesh.scale.x;
        if (mesh.material.opacity < 0.002) mesh.visible = false;
      });
    };

    const animate = () => {
      requestAnimationFrame(animate);
      render();
    };

    const resize = () => {
      if (!currentMount) return;
      const width = window.innerWidth;
      const height = isMobile
        ? window.screen?.height || window.innerHeight
        : window.innerHeight;
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
      composerRef.current.setSize(width, height);
    };

    animate();
    window.addEventListener("resize", () => resize());
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      currentMount.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose();
      window.removeEventListener("resize", () => resize());
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);

      if (statsRef.current) {
        currentMount.removeChild(statsRef.current.dom);
        statsRef.current.dom.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enableGui) return;
    let gui;
    import("dat.gui").then(module => {
      gui = new module.GUI();

      const cameraFolder = gui.addFolder("Camera");
      cameraFolder.add(cameraRef.current.position, "x", -50, 50);
      cameraFolder.add(cameraRef.current.position, "y", -50, 50);
      cameraFolder.add(cameraRef.current.position, "z", -50, 50);

      const cubeRotationFolder = gui.addFolder("Cube rotation");
      cubeRotationFolder.add(
        cubeRef.current.rotation,
        "x",
        -Math.PI,
        Math.PI * 2
      );
      cubeRotationFolder.add(
        cubeRef.current.rotation,
        "y",
        -Math.PI,
        Math.PI * 2
      );
      cubeRotationFolder.add(
        cubeRef.current.rotation,
        "z",
        -Math.PI,
        Math.PI * 2
      );
      cubeRotationFolder.open();

      const cubeScaleFolder = gui.addFolder("Cube scale");
      cubeScaleFolder.add(cubeRef.current.scale, "x", 0, 10);
      cubeScaleFolder.add(cubeRef.current.scale, "y", 0, 10);
      cubeScaleFolder.add(cubeRef.current.scale, "z", 0, 10);
      cubeScaleFolder.open();
    });
    return () => {
      if (gui && enableGui) gui.destroy();
    };
  }, [enableGui]);

  return (
    <>
      {isWebGLAvailable ? (
        <AppWrapper id="app">
          <Scene ref={mountRef} />
        </AppWrapper>
      ) : (
        <ImageContainer>
          <Image src="src/images/hs-icon.png" />
        </ImageContainer>
      )}
    </>
  );
};

export default memo(ThreeJsComponent);
